import { LoadingState } from '@lcs/shared/api';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { RegisterState } from 'src/app/common-components/register/models/register-state';
import {
   RegisterAdapter,
   createRegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import { FaxFilterOptions } from '../../models/fax-filter-option';
import { FaxRegisterOptions } from '../../models/fax-register-options';
import { FaxRegisterListItem } from '../../models/fax-register-results.model';
import * as faxRegisterActions from './outgoing-fax-register.actions';
import { outgoingFaxRegisterAdapterActions } from './outgoing-fax-register.actions';

export const outgoingFaxRegisterFeatureKey = 'outgoingFaxRegister';
export interface OutgoingFaxRegisterState
   extends RegisterState<FaxRegisterListItem, FaxRegisterOptions, OrderingOption> {
   filters: FaxFilterOptions[];
}
const today = new Date();
const thirtyDaysAgo = new Date();
thirtyDaysAgo.setDate(today.getDate() - 30);

const initialFaxQuickFilters: FaxRegisterOptions = {
   typeName: undefined,
   statusName: undefined,
   faxSource: null,
   faxDestination: null,
   faxType: null,
   faxStatus: null,
   searchText: '',
   startDate: thirtyDaysAgo,
   endDate: today,
};

const adapter: RegisterAdapter<FaxRegisterListItem, FaxRegisterOptions, OrderingOption> = createRegisterAdapter<
   FaxRegisterListItem,
   FaxRegisterOptions,
   OrderingOption
>({
   selectId: (faxRegisterItem) => {
      return faxRegisterItem.faxId ?? 0;
   },
});

const initialState: OutgoingFaxRegisterState = {
   ...adapter.getInitialState(),
   filters: [],
   orderingOptions: [{ direction: OrderingDirections.Desc, field: 'createDate' }],
   quickFilters: initialFaxQuickFilters,
};

export const outgoingFaxRegisterReducer = adapter.createReducer(
   faxRegisterActions.outgoingFaxRegisterAdapterActions,
   initialState,
   on(faxRegisterActions.outgoingFaxRegisterAdapterActions.pageEnter, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(faxRegisterActions.outgoingFaxRegisterAdapterActions.loadSuccess, (state, action) => {
      return {
         ...state,
         faxes: action.items,
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         totalCount: action.totalCount,
      };
   }),
   on(faxRegisterActions.outgoingFaxRegisterInitialFilter, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         filters: action.filters,
      };
   }),
   on(outgoingFaxRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(faxRegisterActions.outgoingFaxRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   })
);
export const outgoingFaxRegisterSelector =
   createFeatureSelector<OutgoingFaxRegisterState>(outgoingFaxRegisterFeatureKey);
export const selectors = adapter.getSelectors(outgoingFaxRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectEndCursor,
   selectQuickFilters,
   selectOrderingOptions,
} = adapter.getSelectors(outgoingFaxRegisterSelector);
